import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Mail, Send, X } from 'lucide-react';
import { Invoice } from '../../types/invoice';

interface EmailInvoiceProps {
  invoice: Invoice;
  onClose: () => void;
  onSend: (email: string, subject: string, message: string) => Promise<boolean>;
}

export const EmailInvoice: React.FC<EmailInvoiceProps> = ({
  invoice,
  onClose,
  onSend
}) => {
  const [emailData, setEmailData] = useState({
    to: '',
    subject: `Invoice ${invoice.invoice_number}`,
    message: `Dear ${invoice.customer_name},\n\nPlease find attached your invoice ${invoice.invoice_number} for €${invoice.total_amount.toFixed(2)}.\n\nThank you for your business!\n\nBest regards`
  });
  const [sending, setSending] = useState(false);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!emailData.to.trim()) {
      alert('Please enter customer email address');
      return;
    }

    setSending(true);
    
    try {
      const success = await onSend(emailData.to, emailData.subject, emailData.message);
      if (success) {
        alert('Invoice sent successfully!');
        onClose();
      } else {
        alert('Failed to send invoice');
      }
    } catch (error) {
      alert('Failed to send invoice');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
            <Mail className="h-5 w-5 mr-2" />
            Email Invoice
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSend} className="space-y-4">
          <Input
            label="Customer Email"
            type="email"
            value={emailData.to}
            onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
            placeholder="customer@example.com"
            required
          />

          <Input
            label="Subject"
            value={emailData.subject}
            onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Message
            </label>
            <textarea
              value={emailData.message}
              onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
              rows={6}
              className="input resize-none"
              required
            />
          </div>

          <div className="flex space-x-3">
            <Button
              type="submit"
              variant="primary"
              loading={sending}
              className="flex-1"
            >
              <Send className="h-4 w-4 mr-2" />
              Send Invoice
            </Button>
            <Button
              type="button"
              onClick={onClose}
              variant="secondary"
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};